import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { usePresentationStore } from '@/stores/presentationStore'
import { 
  Presentation,
  Users,
  Settings,
  ArrowRight,
  Copy,
  ExternalLink,
  CheckCircle
} from 'lucide-react'

export function CreateSessionPage() {
  const navigate = useNavigate()
  const [sessionTitle, setSessionTitle] = useState('')
  const [sessionSettings, setSessionSettings] = useState({
    allowIndividualProgress: true,
    showAggregateProgress: true,
    enableAudienceChat: false,
    timePerStage: undefined as number | undefined
  })
  const [isCreating, setIsCreating] = useState(false)
  const [createdSession, setCreatedSession] = useState<{id: string, url: string} | null>(null)
  const [copySuccess, setCopySuccess] = useState(false)

  const { createSession } = usePresentationStore()

  const handleCreateSession = async () => {
    if (!sessionTitle.trim()) return

    setIsCreating(true)
    
    try {
      const sessionId = createSession(sessionTitle.trim(), sessionSettings)
      const sessionUrl = `${window.location.origin}/join/${sessionId}`
      
      setCreatedSession({ id: sessionId, url: sessionUrl })
    } catch (error) {
      console.error('Failed to create session:', error)
    } finally {
      setIsCreating(false)
    }
  }

  const handleCopyUrl = async () => {
    if (!createdSession) return
    
    try {
      await navigator.clipboard.writeText(createdSession.url)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    } catch (err) {
      console.error('Failed to copy URL:', err)
    }
  }

  const handleGoToPresenter = () => {
    navigate('/presenter')
  }

  if (createdSession) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Session Created Successfully!</h1>
            <p className="text-lg text-muted-foreground">Your interactive AI presentation is ready</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Share with Your Audience</CardTitle>
            <CardDescription>
              Send this link to participants to join your presentation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-muted/50 rounded-lg border border-dashed border-muted-foreground/30">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">Session URL:</p>
                  <p className="text-xs text-muted-foreground break-all">{createdSession.url}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyUrl}
                  className="flex-shrink-0"
                >
                  {copySuccess ? (
                    <>Copied!</>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-1" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Session ID: {createdSession.id}</p>
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    Participants can also join using this ID if needed
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={handleGoToPresenter} className="flex-1 gap-2">
                <Presentation className="w-4 h-4" />
                Go to Presenter Dashboard
              </Button>
              <Button 
                variant="outline" 
                onClick={() => window.open(createdSession.url, '_blank')}
                className="gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Test Join
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Next Steps</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-medium text-primary">1</span>
                </div>
                <div>
                  <span className="font-medium">Share the session link</span> with your audience via email, chat, or presentation slide
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-medium text-primary">2</span>
                </div>
                <div>
                  <span className="font-medium">Use the Presenter Dashboard</span> to monitor audience progress and control stages
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-medium text-primary">3</span>
                </div>
                <div>
                  <span className="font-medium">Guide the experience</span> by advancing stages and engaging with participants
                </div>
              </li>
            </ol>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-center space-y-4">
        <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
          <Presentation className="w-8 h-8 text-primary" />
        </div>
        <div>
          <h1 className="text-3xl font-bold">Create Presentation Session</h1>
          <p className="text-lg text-muted-foreground">Set up an interactive AI journey for your audience</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Session Details</CardTitle>
          <CardDescription>
            Configure your presentation session settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2">Session Title</label>
            <input
              type="text"
              value={sessionTitle}
              onChange={(e) => setSessionTitle(e.target.value)}
              placeholder="e.g., 'AI Workshop - Small Business Automation'"
              className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="text-xs text-muted-foreground mt-1">
              This will be visible to participants when they join
            </p>
          </div>

          <div className="space-y-4">
            <h3 className="font-medium flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Session Settings
            </h3>
            
            <div className="grid gap-4">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <div className="font-medium text-sm">Individual Progress</div>
                  <div className="text-xs text-muted-foreground">Allow participants to move at their own pace</div>
                </div>
                <Button
                  variant={sessionSettings.allowIndividualProgress ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSessionSettings(prev => ({
                    ...prev,
                    allowIndividualProgress: !prev.allowIndividualProgress
                  }))}
                >
                  {sessionSettings.allowIndividualProgress ? "Enabled" : "Disabled"}
                </Button>
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <div className="font-medium text-sm">Show Progress</div>
                  <div className="text-xs text-muted-foreground">Display aggregate progress to participants</div>
                </div>
                <Button
                  variant={sessionSettings.showAggregateProgress ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSessionSettings(prev => ({
                    ...prev,
                    showAggregateProgress: !prev.showAggregateProgress
                  }))}
                >
                  {sessionSettings.showAggregateProgress ? "Enabled" : "Disabled"}
                </Button>
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <div className="font-medium text-sm">Audience Chat</div>
                  <div className="text-xs text-muted-foreground">Enable chat between participants</div>
                </div>
                <Button
                  variant={sessionSettings.enableAudienceChat ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSessionSettings(prev => ({
                    ...prev,
                    enableAudienceChat: !prev.enableAudienceChat
                  }))}
                >
                  {sessionSettings.enableAudienceChat ? "Enabled" : "Disabled"}
                </Button>
              </div>
            </div>
          </div>

          <Button
            onClick={handleCreateSession}
            disabled={!sessionTitle.trim() || isCreating}
            className="w-full gap-2"
            size="lg"
          >
            {isCreating ? (
              <>Creating Session...</>
            ) : (
              <>
                Create Session <ArrowRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}